/**
 * OpenRouteService.ts
 * 
 * A utility service for interacting with the OpenRouteService API.
 * Provides functions for:
 * - Calculating routes between locations
 * - Getting GeoJSON data for route visualization
 */

import { withCache } from './CacheService';
import { detectBorderCrossings } from './BorderCrossingService';
import { getGeocode, getRoute, type RouteOptions } from './api/geocodingApi';


// Cache expiration times (in milliseconds)
const GEOCODE_CACHE_EXPIRATION = 30 * 24 * 60 * 60 * 1000; // 30 days for geocoding results
const ROUTE_CACHE_EXPIRATION = 14 * 24 * 60 * 60 * 1000; // 14 days for route results

// Type for route calculation result
export type RouteResult = {
  distance: number; // in meters
  duration: number; // in seconds
  geometry: any; // GeoJSON geometry for the route
  boundingBox: [number, number, number, number]; // [min_lon, min_lat, max_lon, max_lat]
  legDistances?: number[]; // array of distances for each leg of the journey in kilometers
  borderCrossings?: import('../types/BorderCrossingTypes').BorderCrossing[]; // array of border crossings along the route
  /** [lat, lng] for each point: origin, waypoint1, ..., destination (for correct marker positions) */
  waypointCoordinates?: [number, number][];
};

/**
 * Extracts road numbers from the route data
 * @param route The route object from OpenRouteService API response
 * @returns Array of road numbers used in the route
 */
const extractRoadNumbers = (route: any): string[] => {
  const roadNumbers: Set<string> = new Set();

  // Check if route has segments
  if (route.segments && route.segments.length > 0) {
    // Iterate through all segments
    route.segments.forEach((segment: any) => {
      // Check if segment has steps
      if (segment.steps && segment.steps.length > 0) {
        // Iterate through all steps
        segment.steps.forEach((step: any) => {
          // Check for road names in step
          if (step.name) {
            // Look for patterns like "M-1", "E-95", "A-1", "М-4", "Р-255", etc.
            const matches = step.name.match(/([A-ZА-Я]-\d+|\d+[A-ZА-Я]|\b[A-ZА-Я]\d+\b|[A-ZА-Я]-\d+\s+«[^»]+»)/g);
            if (matches) {
              matches.forEach((match: string) => roadNumbers.add(match));
            }
          }
        });
      }
    });
  }

  return Array.from(roadNumbers);
};

/**
 * Geocodes a location name to coordinates
 * @param locationName The name of the location to geocode
 * @param language The language code (e.g., 'ru' or 'en')
 * @returns Promise with coordinates [longitude, latitude]
 */
const geocodeLocationImpl = async (
  locationName: string,
  language: string = 'en'
): Promise<[number, number] | null> => {
  if (!locationName || locationName.trim() === '') {
    console.warn('geocodeLocationImpl called with empty location name');
    return null;
  }

  console.log(`Geocoding location: "${locationName}"`);

  try {
    const coords = await getGeocode(locationName, language);
    if (coords) {
      return [coords.lng, coords.lat];
    }
    return null;
  } catch (err) {
    console.error('Error geocoding location:', err);
    return null;
  }
};

/**
 * Cached version of geocodeLocation
 */
export const geocodeLocation = withCache(
  geocodeLocationImpl,
  'ors:geocode',
  GEOCODE_CACHE_EXPIRATION
);

/**
 * Calculates a route between locations using OpenRouteService API
 * @param locations Array of location names [origin, ...waypoints, destination]
 * @param language The language code (e.g., 'ru' or 'en')
 * @param retryCount Internal parameter for tracking retry attempts
 * @returns Promise with route calculation result
 * 
 * ERROR HANDLING:
 * This function handles authentication errors (403 Forbidden) by providing detailed error messages.
 * Unlike geocodeLocationImpl, this function does not have a direct fallback for route calculation,
 * but it benefits from the geocoding fallback mechanism:
 * 
 * 1. If OpenRouteService geocoding fails, MapQuest geocoding is used as a fallback
 * 2. The coordinates from MapQuest are then used for OpenRouteService route calculation
 * 3. If route calculation fails with a 403 error, a detailed error message is provided
 * 
 * This approach ensures that even if the OpenRouteService geocoding fails due to authentication issues,
 * the route calculation can still proceed using coordinates from MapQuest.
 */
const calculateRouteImpl = async (
  locations: string[],
  language: string = 'en',
  routeOptions?: RouteOptions,
  retryCount: number = 0
): Promise<RouteResult | null> => {
  const maxRetries = 3;
  const baseDelay = 1000; // 1 second base delay

  if (locations.length < 2) {
    return null;
  }

  try {
    // Geocode all locations in parallel for faster loading
    const geocodePromises = locations.map((location) => geocodeLocation(location, language));
    const geocodeResults = await Promise.all(geocodePromises);
    const coordinates: [number, number][] = geocodeResults.filter((c): c is [number, number] => c !== null);

    if (coordinates.length < 2) {
      throw new Error('Failed to geocode enough locations');
    }

    // Маршрут через бэкенд (ключ ORS на сервере), с опциями избегания
    const data = await getRoute(coordinates, language, routeOptions);

    if (data.routes && data.routes.length > 0) {
      const route = data.routes[0];

      // Extract leg distances (ORS: segment.distance in meters)
      const legDistances = route.segments.map((segment: any) => Math.round((segment.distance || 0) / 1000)); // m -> km
      // Total distance: сумма сегментов, чтобы не получить 1 км из summary при мульти-сегментном маршруте
      const totalDistanceM = route.segments?.length
        ? route.segments.reduce((sum: number, seg: any) => sum + (seg.distance || 0), 0)
        : (route.summary?.distance ?? 0);
      const distanceKm = Math.round(totalDistanceM / 1000);

      // Extract bounding box
      const bbox = route.bbox; // [min_lon, min_lat, max_lon, max_lat]

      // Detect border crossings for each segment
      const borderCrossingsPromises = route.segments.map(async (segment: any, index: number) => {
        const segmentStartDistance = index === 0 ? 0 :
          legDistances.slice(0, index).reduce((sum: number, dist: number) => sum + dist, 0);

        return detectBorderCrossings(
          segment.geometry || route.geometry,
          language,
          index,
          segmentStartDistance
        );
      });

      const borderCrossingsArrays = await Promise.all(borderCrossingsPromises);
      const borderCrossings = borderCrossingsArrays.flat();

      console.log('Detected border crossings:', borderCrossings);

      const waypointCoordinates = coordinates.map((c: [number, number]) => [c[1], c[0]] as [number, number]);
      const totalDurationS = route.segments?.length
        ? route.segments.reduce((sum: number, seg: any) => sum + (seg.duration || 0), 0)
        : (route.summary?.duration ?? 0);

      return {
        distance: distanceKm,
        duration: totalDurationS,
        geometry: route.geometry,
        boundingBox: bbox,
        legDistances,
        borderCrossings: borderCrossings.length > 0 ? borderCrossings : undefined,
        waypointCoordinates
      };
    }

    return null;
  } catch (err) {
    console.error('Error calculating route:', err);

    // If it's not a rate limiting error and we haven't exceeded max retries, try again
    if (err.message !== 'Rate limit exceeded for route calculation' && retryCount < maxRetries) {
      const delay = baseDelay * Math.pow(2, retryCount);
      console.log(`Error calculating route. Retrying in ${delay}ms (attempt ${retryCount + 1}/${maxRetries})`);

      // Wait for the calculated delay
      await new Promise(resolve => setTimeout(resolve, delay));

      // Retry the request with incremented retry count
      return calculateRouteImpl(locations, language, routeOptions, retryCount + 1);
    }

    return null;
  }
};

/**
 * Cached version of calculateRoute
 */
export const calculateRoute = withCache(
  calculateRouteImpl,
  'ors:route',
  ROUTE_CACHE_EXPIRATION
);